/**
 * Per-trigger / per-channel notification configuration card.
 *
 * Used by:
 *   • Tickets settings page → `ticketCreatedNotify`
 *   • Calendar settings page → `appointmentBookedNotify`
 *
 * Both fields live on the SAME `ticketSettings` document (server-side
 * truth), so this component is purely a renderer over a
 * `TriggerNotificationConfig` value + onChange. The host page handles
 * loading + saving via `useTicketSettings()` / `useUpdateTicketSettings()`.
 */
import * as React from 'react';
import {
  DEFAULT_TRIGGER_NOTIFICATION_CONFIG,
  type NotificationChannel,
  type NotificationDelay,
  type TriggerNotificationConfig,
} from '@cp3/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useMailTemplates } from '../hooks/useMailTemplates';

const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  email: 'E-Mail',
  whatsapp: 'WhatsApp',
  sms: 'SMS',
};

const DELAY_OPTIONS: ReadonlyArray<{ v: NotificationDelay; label: string }> = [
  { v: 'immediate', label: 'Sofort' },
  { v: '1min', label: 'In 1 Minute' },
  { v: '5min', label: 'In 5 Minuten' },
  { v: '15min', label: 'In 15 Minuten' },
  { v: '1h', label: 'In 1 Stunde' },
  { v: 'endOfDay', label: 'Heute Abend (18:00)' },
];

export function NotificationTriggerCard({
  title,
  description,
  value,
  onChange,
}: {
  title: string;
  description: string;
  value: TriggerNotificationConfig | undefined;
  onChange: (v: TriggerNotificationConfig) => void;
}) {
  const cfg = value ?? DEFAULT_TRIGGER_NOTIFICATION_CONFIG;
  const templates = useMailTemplates();

  function patchChannel(
    ch: NotificationChannel,
    partial: Partial<TriggerNotificationConfig['email']>,
  ) {
    onChange({ ...cfg, [ch]: { ...cfg[ch], ...partial } });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {(Object.keys(CHANNEL_LABELS) as NotificationChannel[]).map((ch) => {
          const c = cfg[ch];
          return (
            <div
              key={ch}
              className="grid gap-2 rounded-md border border-border p-3 sm:grid-cols-[1fr_auto] sm:items-center"
            >
              <div className="flex items-center justify-between gap-3">
                <Label className="cursor-pointer text-sm font-medium">
                  {CHANNEL_LABELS[ch]}
                </Label>
                <Switch
                  checked={c.enabled}
                  onCheckedChange={(v) => patchChannel(ch, { enabled: v })}
                />
              </div>
              {c.enabled && (
                <div className="grid gap-2 sm:col-span-2 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                      Vorlage
                    </Label>
                    <Select
                      value={c.templateId || '__default'}
                      onValueChange={(v) =>
                        patchChannel(ch, { templateId: v === '__default' ? '' : v })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__default">Eingebaute Vorlage</SelectItem>
                        {(templates.data ?? []).map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                      Versand
                    </Label>
                    <Select
                      value={c.sendDelay}
                      onValueChange={(v) =>
                        patchChannel(ch, { sendDelay: v as NotificationDelay })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DELAY_OPTIONS.map((o) => (
                          <SelectItem key={o.v} value={o.v}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
